import { Button, ButtonLevel, ButtonSize, LinkSize, RadialProgress, Text, TextColor, TextSize } from '@vizit/atoms';
import { VizitLogo } from '@vizit/icons';
import { Heading, HeadingVariant, RouterLink } from '@vizit/molecules';
import * as React from 'react';
import { SsoErrorAlert, SsoLoginError } from '../sso-error-alert';

type SsoDirectLoginError = 'not_found' | 'network';

interface SsoDirectLoginProps {
  orgSlug: string;
  loginHint?: string;
  onAuthorize: (orgSlug: string, loginHint: string) => Promise<string>;
}

function SsoDirectLogin(props: SsoDirectLoginProps) {
  const { orgSlug, loginHint = '', onAuthorize } = props;
  const hasStarted = React.useRef(false);
  const mountedRef = React.useRef(false);
  const [error, setError] = React.useState<SsoDirectLoginError | null>(null);

  const handleAuthorize = React.useCallback(async () => {
    try {
      const authorizeUrl = await onAuthorize(orgSlug, loginHint);
      if (mountedRef.current) {
        window.location.href = authorizeUrl;
      }
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      const status = (err as { status?: number })?.status;
      setError(status === 404 ? 'not_found' : 'network');
    }
  }, [onAuthorize, orgSlug, loginHint]);

  React.useEffect(() => {
    mountedRef.current = true;

    if (!hasStarted.current) {
      hasStarted.current = true;
      handleAuthorize();
    }

    return () => {
      mountedRef.current = false;
    };
  }, [handleAuthorize]);

  if (error) {
    return (
      <div className="flex-grow flex flex-col justify-center items-center gap-16" data-testid="sso-direct-login-error">
        <div className="flex flex-col items-center gap-16">
          <VizitLogo aria-label="Vizit Logo" />
          <Heading variant={HeadingVariant.H5}>Sign-in error</Heading>
        </div>
        <div className="flex flex-col justify-center items-center gap-8">
          <SsoErrorAlert error={error === 'not_found' ? SsoLoginError.GenericError : SsoLoginError.GenericError} />
          {error === 'network' && (
            <Button
              level={ButtonLevel['Text-Primary']}
              size={ButtonSize.Sm}
              data-testid="sso-direct-login-retry"
              onPress={() => {
                setError(null);
                hasStarted.current = false;
                handleAuthorize();
              }}
            >
              Retry
            </Button>
          )}
          <RouterLink size={LinkSize.Sm} to="/sso" data-testid="sso-direct-login-back-link">
            Back to login
          </RouterLink>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-grow flex flex-col justify-center items-center gap-16" data-testid="sso-direct-login-page">
      <div className="flex flex-col items-center gap-16">
        <VizitLogo aria-label="Vizit Logo" />
        <div className="flex flex-col items-center justify-center gap-4 min-h-[70px]">
          <Heading variant={HeadingVariant.H5}>Log in to your account</Heading>
          <Text size={TextSize.Md} color={TextColor.Tertiary}>
            Redirecting to your identity provider.
          </Text>
          <RadialProgress isIndeterminate size={16} aria-label="Redirecting to your identity provider" />
        </div>
      </div>
    </div>
  );
}

export { SsoDirectLogin };
export type { SsoDirectLoginProps };
